import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { checkCrossword, completePlay } from "../gamesApi";
import {
  afterTyping,
  beforeCell,
  buildPuzzle,
  clearStatusesAt,
  directionAt,
  emptyLetters,
  entryAt,
  entryStart,
  isFull,
  moveCursor,
  nextEntry,
  normalizeLetter,
  orderedEntries,
  proofRows,
  statusMap,
  toggleDirection,
  wrongCells,
} from "./crosswordEngine";

const ARROWS = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
const LABELS = { across: "Across", down: "Down" };

/**
 * Crossword level. The payload has the grid shape, numbers and clues but no
 * answers: Check (and a full grid) sends the letters to the server through
 * the signed play session (checkCrossword), which replies per entry whether
 * it is right. When every entry is right the letters are submitted with
 * completePlay; the server verifies them again before recording the
 * completion (signed-out players get the result but nothing is recorded).
 *
 * Keyboard: type to fill (the cursor moves along the current entry), arrows
 * move between squares (blocks are skipped), Space or clicking the current
 * square switches Across / Down, Tab / Shift+Tab go to the next / previous
 * entry (past the last one focus leaves the grid), Backspace clears and steps
 * back, Enter checks. Each square is a text input so phones show their
 * keyboard.
 */
function Crossword({ layout, gameTitle, levelName, backTo, playToken, playError, onRoundStart, onComplete, completionSlot }) {
  const puzzle = useMemo(() => buildPuzzle(layout), [layout]);
  const first = puzzle ? orderedEntries(puzzle)[0] : null;
  const [letters, setLetters] = useState(() => (puzzle ? emptyLetters(puzzle) : []));
  const [cursor, setCursor] = useState(() => (first ? first.cells[0] : [0, 0]));
  const [direction, setDirection] = useState(first ? first.direction : "across");
  const [statuses, setStatuses] = useState({});
  const [phase, setPhase] = useState("play");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const inputs = useRef({});
  const focusCell = useRef(false);
  const focusResult = useRef(false);
  const resultRef = useRef(null);
  const busyRef = useRef(false);
  const autoRef = useRef(false);
  const pointerOnCursor = useRef(false);

  useEffect(() => {
    if (onRoundStart) onRoundStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (focusCell.current) {
      focusCell.current = false;
      const el = inputs.current[cursor[0] + ":" + cursor[1]];
      if (el && document.activeElement !== el) el.focus();
    }
    if (focusResult.current && resultRef.current) {
      focusResult.current = false;
      resultRef.current.focus();
    }
  });

  const finish = (rows) => {
    busyRef.current = true;
    setPhase("finishing");
    setError("");
    completePlay(playToken, { grid: rows }).then((res) => {
      busyRef.current = false;
      if (res.ok) {
        setPhase("complete");
        focusResult.current = true;
        if (onComplete) onComplete({ completion: res.data.completion || null });
      } else {
        setError(res.error);
        setPhase("finish_error");
      }
    });
  };

  const check = () => {
    if (!puzzle || busyRef.current || !playToken || phase !== "play") return;
    const rows = proofRows(puzzle, letters);
    busyRef.current = true;
    setPhase("checking");
    setError("");
    checkCrossword(playToken, rows).then((res) => {
      busyRef.current = false;
      if (!res.ok) {
        setError(res.error);
        setPhase("play");
        return;
      }
      const map = statusMap(res.data);
      setStatuses(map);
      if (res.data.complete) {
        finish(rows);
        return;
      }
      const values = Object.keys(map).map((k) => map[k]);
      const wrong = values.filter((s) => s === "incorrect").length;
      const right = values.filter((s) => s === "correct").length;
      setMessage(
        wrong
          ? wrong + (wrong === 1 ? " answer is" : " answers are") + " not right yet (marked in red). " + right + " of " + values.length + " correct."
          : right + " of " + values.length + " answers correct so far. Keep going!"
      );
      setPhase("play");
    });
  };

  // The grid is checked automatically when it becomes full; later edits of a full grid wait for Check.
  useEffect(() => {
    const full = !!puzzle && isFull(puzzle, letters);
    if (!full) {
      autoRef.current = false;
    } else if (!autoRef.current && phase === "play" && playToken && !busyRef.current) {
      autoRef.current = true;
      check();
    }
  });

  if (!puzzle) {
    return (
      <div className="games-panel games-panel--empty" role="alert">
        <p className="games-panel__body">This crossword isn’t ready yet.</p>
      </div>
    );
  }

  const back = backTo ? (
    <Link to={backTo.path} className="btn btn-outline-secondary crossword__back">
      {backTo.label}
    </Link>
  ) : null;

  const restart = () => {
    busyRef.current = false;
    autoRef.current = false;
    const start = orderedEntries(puzzle)[0];
    setLetters(emptyLetters(puzzle));
    setStatuses({});
    setCursor(start.cells[0]);
    setDirection(start.direction);
    setMessage("");
    setError("");
    setPhase("play");
    focusCell.current = true;
    if (onRoundStart) onRoundStart();
  };

  if (phase === "complete") {
    return (
      <section className="crossword crossword--result" aria-labelledby="crossword-result-title" data-testid="crossword-result">
        <h3 id="crossword-result-title" className="crossword__result-title" tabIndex={-1} ref={resultRef}>
          Crossword complete!
        </h3>
        <p className="crossword__result-level">
          {gameTitle} · {levelName} — every answer is correct.
        </p>
        <div className="crossword__grid is-solved" style={{ "--cw-cols": puzzle.cols }} aria-hidden="true">
          {puzzle.open.map((line, r) =>
            line.map((isOpen, c) =>
              isOpen ? (
                <div key={r + ":" + c} className="crossword__cell is-correct">
                  {puzzle.numbers[r + ":" + c] ? <span className="crossword__num">{puzzle.numbers[r + ":" + c]}</span> : null}
                  <span className="crossword__letter">{letters[r][c]}</span>
                </div>
              ) : (
                <div key={r + ":" + c} className="crossword__cell is-block" />
              )
            )
          )}
        </div>
        <div className="crossword__actions">
          <button type="button" className="btn btn-danger" onClick={restart}>
            Replay
          </button>
          {back}
        </div>
        {completionSlot}
      </section>
    );
  }

  const active = entryAt(puzzle, cursor[0], cursor[1], direction);
  const activeCells = {};
  if (active) {
    active.cells.forEach(([r, c]) => {
      activeCells[r + ":" + c] = true;
    });
  }
  const wrong = wrongCells(puzzle, statuses);

  const select = (r, c, dir) => {
    setCursor([r, c]);
    setDirection(dir);
    focusCell.current = true;
  };

  const put = (r, c, letter, advance) => {
    const next = letters.map((row) => row.slice());
    next[r][c] = letter;
    setLetters(next);
    setStatuses((prev) => clearStatusesAt(puzzle, prev, r, c));
    setMessage("");
    if (phase === "finish_error") {
      setPhase("play");
      setError("");
    }
    if (advance && letter) {
      const entry = entryAt(puzzle, r, c, direction);
      if (entry) {
        const [nr, nc] = afterTyping(entry, r, c, next);
        select(nr, nc, entry.direction);
      }
    }
  };

  const onKeyDown = (e, r, c) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (ARROWS[e.key]) {
      e.preventDefault();
      const [dr, dc] = ARROWS[e.key];
      const target = moveCursor(puzzle, r, c, dr, dc);
      if (target) select(target[0], target[1], directionAt(puzzle, target[0], target[1], dr ? "down" : "across"));
      return;
    }
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      setDirection(toggleDirection(puzzle, r, c, direction));
      return;
    }
    if (e.key === "Tab") {
      const entry = active ? nextEntry(puzzle, active.key, e.shiftKey ? -1 : 1) : null;
      if (entry) {
        e.preventDefault();
        const [sr, sc] = entryStart(entry, letters);
        select(sr, sc, entry.direction);
      }
      return;
    }
    if (e.key === "Backspace") {
      e.preventDefault();
      if (letters[r][c]) {
        put(r, c, "", false);
      } else if (active) {
        const [pr, pc] = beforeCell(active, r, c);
        if (pr !== r || pc !== c) {
          put(pr, pc, "", false);
          select(pr, pc, active.direction);
        }
      }
      return;
    }
    if (e.key === "Delete") {
      e.preventDefault();
      put(r, c, "", false);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      check();
      return;
    }
    if (/^[a-zA-Z]$/.test(e.key)) {
      e.preventDefault();
      put(r, c, e.key.toUpperCase(), true);
    }
  };

  const cellLabel = (r, c) => {
    const parts = ["Row " + (r + 1) + ", column " + (c + 1)];
    ["across", "down"].forEach((dir) => {
      const key = (puzzle.cellEntries[r + ":" + c] || {})[dir];
      const entry = key ? puzzle.byKey[key] : null;
      if (entry) {
        const index = entry.cells.findIndex(([er, ec]) => er === r && ec === c);
        parts.push(entry.number + " " + LABELS[dir] + ", letter " + (index + 1) + " of " + entry.length);
      }
    });
    return parts.join(", ");
  };

  const busy = phase === "checking" || phase === "finishing";
  const clueList = (dir) => (
    <div className="crossword__clues-group">
      <h4 className="crossword__clues-title">{LABELS[dir]}</h4>
      <ol className="crossword__clues-list">
        {orderedEntries(puzzle)
          .filter((entry) => entry.direction === dir)
          .map((entry) => {
            const status = statuses[entry.key];
            const isActive = active && active.key === entry.key;
            return (
              <li key={entry.key}>
                <button
                  type="button"
                  className={"crossword__clue" + (isActive ? " is-active" : "") + (status ? " is-" + status : "")}
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => {
                    const [sr, sc] = entryStart(entry, letters);
                    select(sr, sc, entry.direction);
                  }}
                  data-entry={entry.key}
                >
                  <span className="crossword__clue-number">{entry.number}</span>
                  <span className="crossword__clue-text">
                    {entry.clue} ({entry.length})
                  </span>
                  {status === "correct" ? <span className="crossword__clue-mark">✓<span className="sr-only"> correct</span></span> : null}
                  {status === "incorrect" ? <span className="crossword__clue-mark">✗<span className="sr-only"> not right yet</span></span> : null}
                </button>
              </li>
            );
          })}
      </ol>
    </div>
  );

  return (
    <section className="crossword" aria-label={"Crossword: " + levelName} data-testid="crossword">
      <p className="crossword__current" aria-live="polite" data-testid="crossword-current">
        {active ? (
          <React.Fragment>
            <strong>
              {active.number} {LABELS[active.direction]}
            </strong>{" "}
            {active.clue} ({active.length})
          </React.Fragment>
        ) : null}
      </p>
      <div className="crossword__layout">
        <div className="crossword__board">
          <div className="crossword__grid" style={{ "--cw-cols": puzzle.cols }} role="group" aria-label="Crossword grid" data-testid="crossword-grid">
            {puzzle.open.map((line, r) =>
              line.map((isOpen, c) => {
                const id = r + ":" + c;
                if (!isOpen) return <div key={id} className="crossword__cell is-block" aria-hidden="true" />;
                const isCursor = cursor[0] === r && cursor[1] === c;
                return (
                  <div
                    key={id}
                    className={"crossword__cell" + (activeCells[id] ? " is-active-entry" : "") + (isCursor ? " is-cursor" : "") + (wrong[id] ? " is-wrong" : "")}
                    data-cell={id}
                  >
                    {puzzle.numbers[id] ? (
                      <span className="crossword__num" aria-hidden="true">
                        {puzzle.numbers[id]}
                      </span>
                    ) : null}
                    <input
                      ref={(el) => {
                        inputs.current[id] = el;
                      }}
                      className="crossword__input"
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                      value={letters[r][c]}
                      tabIndex={isCursor ? 0 : -1}
                      aria-label={cellLabel(r, c)}
                      aria-invalid={wrong[id] ? "true" : undefined}
                      onPointerDown={() => {
                        pointerOnCursor.current = isCursor && document.activeElement === inputs.current[id];
                      }}
                      onClick={() => {
                        if (pointerOnCursor.current) setDirection(toggleDirection(puzzle, r, c, direction));
                        pointerOnCursor.current = false;
                      }}
                      onFocus={() => {
                        if (!isCursor) {
                          setCursor([r, c]);
                          setDirection(directionAt(puzzle, r, c, direction));
                        }
                      }}
                      onKeyDown={(e) => onKeyDown(e, r, c)}
                      onChange={(e) => put(r, c, normalizeLetter(e.target.value), true)}
                    />
                  </div>
                );
              })
            )}
          </div>
          <div className="crossword__actions">
            <button type="button" className="btn btn-danger crossword__check" onClick={check} disabled={busy || !playToken} data-testid="crossword-check">
              {phase === "checking" ? "Checking…" : phase === "finishing" ? "Finishing…" : "Check"}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={restart} disabled={busy}>
              Clear grid
            </button>
            {back}
          </div>
          <div className="crossword__status" role="status" aria-live="polite" data-testid="crossword-status">
            {playError ? null : !playToken ? "Connecting… you can start filling in the grid." : message}
          </div>
          {playError ? (
            <div className="crossword__error" role="alert">
              <p>{playError}</p>
              <button type="button" className="btn btn-danger" onClick={() => onRoundStart && onRoundStart()}>
                Try again
              </button>
            </div>
          ) : null}
          {error ? (
            <div className="crossword__error" role="alert">
              <p>{error}</p>
              {phase === "finish_error" ? (
                <button type="button" className="btn btn-danger" onClick={() => finish(proofRows(puzzle, letters))}>
                  Try again
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="crossword__clues" aria-label="Clues" role="region">
          {clueList("across")}
          {clueList("down")}
        </div>
      </div>
    </section>
  );
}

export default Crossword;
